import { EventMeta } from '../../../interfaces/meta.interface';

type FileDownloadedFields = {
    ObjectId: string;
};

const FileDownloaded: EventMeta<FileDownloadedFields> = {
    title: 'SharePoint File Downloaded',
    default: {
        description: 'SharePoint File Downloaded',
        longDescription: 'A user downloaded a file.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // file downloaded: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP downloaded a SharePoint file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to access or exfiltrate sensitive information without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP.  allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. The action originated from a suspicious IP included in your allowed IP list. While the IP range itself is normally trusted, the specific IP has been flagged as suspicious, which makes the download unusual. Review this activity to confirm whether the file access was legitimate or potentially unauthorized, especially if the downloaded file was sensitive or if repeated activity is observed from the same source.`,
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. The action originated from a suspicious IP located in your allowed region. Although the region itself is normally trusted, the IP address has been flagged as suspicious, making the download unusual. Review this activity to determine whether the file access was expected or potentially unauthorized, particularly if the file was sensitive or if repeated downloads occur from similar flagged IPs.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. The action originated from a suspicious IP located in one of your allowed countries. Although the country itself is normally trusted under your policies, the IP address has been flagged as suspicious, which makes the activity unusual. Review the file download details to confirm whether the access was legitimate or potentially unauthorized, especially if sensitive documents were involved or if multiple downloads are observed from flagged IPs.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. The action originated from a suspicious IP included in your allowed ASN list. Although the ASN itself is normally trusted, the individual IP has been flagged as suspicious. This makes the download unusual and worth closer review, especially if the file contained sensitive content or if repeated downloads occur from the same flagged IP range.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) =>
                `An unapproved IP outside your allowed ASN, IPs, region, or country downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. The action originated from an unapproved IP outside all of your allowed ASNs, IPs, regions, and countries. Because the download came from entirely outside your defined trusted boundaries, it is unusual. While this does not automatically indicate malicious intent, the activity should be reviewed carefully, particularly if the file was sensitive or if similar access is repeatedly attempted from unapproved sources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // file downloaded: user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to download a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint with an account that does not belong to your organisation. The action originated from an unapproved IP outside your allowed list. While file downloads are common, the combination of an external account and an unapproved IP makes the activity unusual. Review the download to confirm whether it was expected or potentially unauthorized, especially if sensitive files were involved.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // file downloaded: microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. This was recorded as a Microsoft system operation event, with the IP address tied to the server responsible for synchronization. This is an expected background activity and is informational in nature. No action is required unless downloads attributed to Microsoft system processes appear inconsistent with your configuration or expected usage.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // file downloaded
        {
            severity: 'info',
            description: (fields) => `A file was downloaded from SharePoint`,
            longDescription: (fields) =>
                `A file was downloaded from SharePoint. This is an informational record of routine activity. No unusual conditions were associated with this entry. No action is required unless the download was unexpected or coincides with other anomalies in user behaviour.`,
        },
    ],
};

export default FileDownloaded;
